import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Grape, ShieldAlert } from "lucide-react";

const STORAGE_KEY = "vinonotas_age_ok";
const THIRTY_DAYS = 30 * 24 * 3600 * 1000;
const C = { borgona: "#5A1E2C", oro: "#C8A96A", vino: "#8B2635" };
const BG = "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function isVerified() {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    return ts && Date.now() - ts < THIRTY_DAYS;
  } catch {
    return false;
  }
}

export default function AgeGate() {
  const [show, setShow] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!isVerified()) setShow(true);
  }, []);

  const confirm = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) { void e; }
    setShow(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="age-gate"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[999] flex items-center justify-center p-5"
      >
        <div className="absolute inset-0">
          <img src={BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,8,12,0.9) 0%, rgba(20,8,12,0.96) 100%)" }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md rounded-3xl bg-[#F7F4EF] shadow-2xl overflow-hidden"
        >
          <div className="px-8 pt-9 pb-8 text-center">
            <span className="inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-6" style={{ background: C.borgona }}>
              <Grape className="w-8 h-8" style={{ color: C.oro }} />
            </span>

            {!denied ? (
              <>
                <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Verificación de edad</p>
                <h2 className="font-serif text-3xl mb-3" style={{ color: C.borgona }}>¿Eres mayor de edad?</h2>
                <p className="text-sm leading-relaxed text-neutral-600 mb-8">
                  Vinonotas contiene contenido relacionado con bebidas alcohólicas. Para continuar debes tener la edad
                  legal para consumir alcohol en tu país de residencia.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    data-testid="age-gate-yes"
                    onClick={confirm}
                    className="w-full py-3.5 rounded-full text-white font-semibold transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                    style={{ background: C.borgona }}
                  >
                    Sí, soy mayor de edad
                  </button>
                  <button
                    data-testid="age-gate-no"
                    onClick={() => setDenied(true)}
                    className="w-full py-3.5 rounded-full font-semibold border transition-colors hover:bg-black/[0.03]"
                    style={{ borderColor: "rgba(0,0,0,0.15)", color: C.borgona }}
                  >
                    No
                  </button>
                </div>
                <p className="text-[11px] text-neutral-400 mt-6">
                  Al continuar aceptas nuestros Términos y la Política de Privacidad. Bebe con moderación.
                </p>
              </>
            ) : (
              <div data-testid="age-gate-denied">
                <span className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-5" style={{ background: "rgba(139,38,53,0.1)" }}>
                  <ShieldAlert className="w-7 h-7" style={{ color: C.vino }} />
                </span>
                <h2 className="font-serif text-2xl mb-3" style={{ color: C.borgona }}>Lo sentimos</h2>
                <p className="text-sm leading-relaxed text-neutral-600">
                  Debes ser mayor de edad legal para acceder a Vinonotas. No es posible continuar en este momento.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
